import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { KanbanSquare, Plus, Trash2, UserCheck, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { usePipelineEtapes, usePipelines } from '@/hooks/queries/useCrmPipelines';
import {
  useBulkUpdateCommercial,
  useDeleteOpportunite,
  useOpportunites,
  type OpportunitesFilter,
} from '@/hooks/queries/useCrmOpportunites';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRole } from '@/hooks/useRole';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { OpportuniteRow, OpportuniteStatut } from '@/lib/database.types';
import { OpportuniteFormDialog } from './OpportuniteFormDialog';

const STATUT_VARIANT: Record<
  OpportuniteStatut,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'
> = {
  open: 'outline',
  won: 'success',
  lost: 'destructive',
  archived: 'secondary',
};

export function CrmListePage(): JSX.Element {
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const { data: entreprises = [] } = useEntreprises();
  const { data: users = [] } = useUsersList();

  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: pipelines = [] } = usePipelines(entrepriseId);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [filter, setFilter] = useState<OpportunitesFilter>({});
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: etapesActivePipeline = [] } = usePipelineEtapes(filter.pipelineId ?? null);

  const fullFilter: OpportunitesFilter = {
    ...filter,
    entrepriseId,
    search,
    page,
    pageSize: 50,
  };

  const { data, isLoading } = useOpportunites(fullFilter);
  const rows = data?.rows ?? [];
  const total = data?.count ?? 0;

  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });
  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);

  const pipelineMap = useMemo(() => {
    const m = new Map<string, { nom: string; couleur: string }>();
    for (const p of pipelines) m.set(p.id, { nom: p.nom, couleur: p.couleur });
    return m;
  }, [pipelines]);

  const removeOp = useDeleteOpportunite();
  const bulkAssign = useBulkUpdateCommercial();

  const [selectedRows, setSelectedRows] = useState<OpportuniteRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);
  const selectedIds = selectedRows.map((r) => r.id);

  const columns = useMemo<ColumnDef<OpportuniteRow>[]>(
    () => [
      {
        accessorKey: 'reference',
        header: 'Réf.',
        size: 130,
        cell: ({ row }) => (
          <Link
            to={`/crm/opportunite/${row.original.id}`}
            className="font-mono text-xs text-primary hover:underline"
          >
            {row.original.reference}
          </Link>
        ),
      },
      {
        accessorKey: 'titre',
        header: 'Titre',
        cell: ({ getValue }) => <span className="line-clamp-1 text-xs">{String(getValue())}</span>,
      },
      {
        accessorKey: 'pipeline_id',
        header: 'Pipeline',
        size: 160,
        cell: ({ getValue }) => {
          const p = pipelineMap.get(getValue() as string);
          if (!p) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: p.couleur }}>
              <span className="h-2 w-2 rounded-full" style={{ background: p.couleur }} />
              {p.nom}
            </span>
          );
        },
      },
      {
        accessorKey: 'tier_id',
        header: 'Client',
        size: 200,
        cell: ({ getValue }) => {
          const id = getValue() as string | null;
          return (
            <span className="line-clamp-1 text-xs">{id ? (tierMap.get(id) ?? '—') : '—'}</span>
          );
        },
      },
      {
        accessorKey: 'commercial_id',
        header: 'Commercial',
        size: 160,
        cell: ({ getValue }) => {
          const id = getValue() as string | null;
          return <span className="text-xs">{id ? (userMap.get(id) ?? '—') : '—'}</span>;
        },
      },
      {
        accessorKey: 'montant_estime_dzd',
        header: 'Montant',
        size: 120,
        cell: ({ getValue }) => <span className="num text-xs">{formatDA(Number(getValue()))}</span>,
      },
      {
        accessorKey: 'probabilite',
        header: 'Prob.',
        size: 70,
        cell: ({ getValue }) => <span className="num text-xs">{Number(getValue())}%</span>,
      },
      {
        accessorKey: 'date_cloture_prevue',
        header: 'Clôture prév.',
        size: 110,
        cell: ({ getValue }) => (
          <span className="num text-xs">{getValue() ? formatDateDZ(String(getValue())) : '—'}</span>
        ),
      },
      {
        accessorKey: 'statut',
        header: 'Statut',
        size: 100,
        cell: ({ getValue }) => {
          const s = getValue() as OpportuniteStatut;
          return <Badge variant={STATUT_VARIANT[s]}>{t(`crm.statut.${s}`)}</Badge>;
        },
      },
    ],
    [pipelineMap, tierMap, userMap],
  );

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('crm.nav.liste')}
        subtitle="Toutes les opportunités, tous pipelines confondus."
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={!entrepriseId || pipelines.length === 0}
            >
              <Plus className="h-4 w-4" />
              {t('crm.actions.addOpportunite')}
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3">
        <FilterSelect
          label="Pipeline"
          value={filter.pipelineId ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, pipelineId: v || null, etapeId: null }))}
          options={[
            { value: '', label: '— Tous —' },
            ...pipelines.map((p) => ({ value: p.id, label: p.nom })),
          ]}
        />
        <FilterSelect
          label="Étape"
          value={filter.etapeId ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, etapeId: v || null }))}
          options={[
            { value: '', label: '— Toutes —' },
            ...etapesActivePipeline.map((e) => ({ value: e.id, label: e.nom })),
          ]}
        />
        <FilterSelect
          label="Statut"
          value={filter.statut ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, statut: (v as OpportuniteStatut) || null }))}
          options={[
            { value: '', label: '— Tous —' },
            { value: 'open', label: 'Ouverte' },
            { value: 'won', label: 'Gagnée' },
            { value: 'lost', label: 'Perdue' },
            { value: 'archived', label: 'Archivée' },
          ]}
        />
        <FilterSelect
          label="Commercial"
          value={filter.commercialId ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, commercialId: v || null }))}
          options={[
            { value: '', label: '— Tous —' },
            ...users.map((u) => ({
              value: u.user_id,
              label: u.full_name ?? u.user_id.slice(0, 8),
            })),
          ]}
        />
        <div className="ml-auto space-y-1">
          <label className="text-xs text-muted-foreground">Recherche</label>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Réf, titre…"
            className="h-9 w-56"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setFilter({});
            setSearchInput('');
            setPage(0);
          }}
        >
          <XCircle className="h-3.5 w-3.5" /> Effacer
        </Button>
      </div>

      {entrepriseId && pipelines.length === 0 ? (
        <EmptyState
          icon={<KanbanSquare className="h-6 w-6" />}
          title={`Aucun pipeline configuré pour ${entreprises.find((e) => e.id === entrepriseId)?.nom ?? 'cette entreprise'}`}
          description="Créez votre premier pipeline pour commencer à suivre vos opportunités commerciales."
          action={
            <Button asChild>
              <Link to={`/parametres/crm/pipelines?entreprise=${entrepriseId}`}>
                → Configurer un pipeline
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <DataTable
            key={selectionVersion}
            data={rows}
            columns={columns}
            loading={isLoading}
            pageSize={50}
            searchPlaceholder="Rechercher…"
            exportFileName="opportunites.csv"
            enableSelection
            getRowId={(row) => row.id}
            onSelectionChange={setSelectedRows}
            bulkBar={
              <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
                <div className="flex items-center gap-1">
                  <select
                    value={bulkAssignTo}
                    onChange={(e) => setBulkAssignTo(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-xs"
                  >
                    <option value="">— Réassigner à —</option>
                    {users.map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.full_name ?? u.user_id.slice(0, 8)}
                      </option>
                    ))}
                  </select>
                  <BulkButton
                    onClick={async () => {
                      if (!bulkAssignTo) return;
                      const r = await bulkAssign.mutateAsync({
                        ids: selectedIds,
                        commercial_id: bulkAssignTo,
                      });
                      if (r.errors.length === 0)
                        toast.success(`${r.success.length} opportunité(s) réassignée(s)`);
                      else toast.warning(`${r.success.length} OK · ${r.errors.length} échec(s)`);
                      clearSelection();
                      setBulkAssignTo('');
                    }}
                  >
                    <UserCheck className="h-3.5 w-3.5" /> Appliquer
                  </BulkButton>
                </div>
                {isAdmin && (
                  <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </BulkButton>
                )}
              </BulkActionBar>
            }
          />

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {total} opportunité{total > 1 ? 's' : ''} · page {page + 1} / {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </>
      )}

      <OpportuniteFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        entrepriseId={entrepriseId}
        editing={null}
        onSaved={(opp) => {
          setCreateOpen(false);
          if (opp) navigate(`/crm/opportunite/${opp.id}`);
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selectedRows.length} opportunité${selectedRows.length > 1 ? 's' : ''} ?`}
        description="Action irréversible — toutes les activités, valeurs custom et historique liés seront supprimés."
        consequences={[
          `${selectedRows.length} ligne(s) effacées`,
          'Activités, champs custom, historique et contacts liés supprimés en cascade',
          'Audit log conservé',
        ]}
        challenge="SUPPRIMER"
        loading={removeOp.isPending}
        onConfirm={async () => {
          let ok = 0;
          for (const id of selectedIds) {
            try {
              await removeOp.mutateAsync(id);
              ok++;
            } catch (e) {
              toast.error((e as Error).message);
            }
          }
          toast.success(`${ok} opportunité(s) supprimée(s)`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />

      <span className={cn('hidden')}>{bulkAssign.isPending ? '' : ''}</span>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
